/**
 * 🧱 Universal Series Card Component (SeriesCard.js)
 * 
 * 모든 작품 카드를 그리는 표준 컴포넌트입니다.
 * 마케팅, 심의, 큐레이션 로직이 원자(Atomic) 단위로 내장되어 있습니다.
 * 
 * @param {Object} props
 * @param {Object} props.series - 작품 데이터 (필수)
 * @param {Object} props.options - 렌더링 옵션
 * @param {Number} props.options.rank - 순위 (1~3: 메달 컬러, 4+: 일반)
 * @param {Array<String>} props.options.badges - 추가 뱃지 ('new', 'best', 'editor' ...)
 * @param {Object} props.options.marketing - 마케팅 데이터 { multiplier, theme }
 * @param {Boolean} props.options.overlayStats - 하단 그라디언트 통계 표시 여부
 * @param {Boolean} props.options.isBig - 큰 카드(Hero) 모드
 * @param {Function} props.onClick - 클릭 핸들러 (없으면 기본 라우팅)
 */
export const SeriesCard = (series, options = {}) => {
    // 1. 방어 코드 (Safety First)
    if (!series) return document.createElement('div');

    const card = document.createElement('div');
    const seriesId = series.id;
    const {
        rank,
        badges = [],
        marketing,
        overlayStats = false,
        isBig = false,
        showType = false
    } = options;

    // 2. Base Classes
    // 가로 사이즈는 부모 컨테이너(Grid/Flex)에 의해 결정되므로 w-full 사용
    // snap-start 등은 리스트 컨테이너에서 필요 시 주입해야 하나? 
    // -> SeriesCard는 "Card 내부"만 담당하고, wrapper 클래스는 외부에서 병합하는 것이 좋으나
    //    유지보수 편의상 기본 클래스를 여기서 정의하고 외부 클래스를 append 하는 방식 고려.
    //    일단 여기서는 순수 카드 DOM만 반환.

    card.dataset.seriesId = seriesId;
    card.className = "relative group cursor-pointer overflow-hidden rounded-xl bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 hover:shadow-lg transition-all duration-300"; // Light Theme Default

    // Click Event
    card.onclick = options.onClick || (() => window.router.navigate(`/series/${seriesId}`));

    // 3. Image Handling
    const imageUrl = series.coverImage || series.thumbnail;
    const hasImage = !!imageUrl;

    // 4. Marketing Theme (Event)
    // marketing: { multiplier: 2, theme: { borderRing, badgeBg, ... } }
    // Image Container Border
    let borderClass = "border border-zinc-700/50"; // Inner image default border (slightly dark to frame image)
    if (marketing && marketing.theme) {
        // Replace base border with Event Ring
        borderClass = `border-2 ${marketing.theme.borderRing.replace('ring-', 'border-')}`; // Tailwind hack if needed, or use ring util
        // actually ring is better for layout stability, but we use border for explicit visual.
        // Let's use user's preferred ring logic if passed, or explicit border color.
        // Assuming marketing.theme has 'border-yellow-500' or similar.
        // If it sends 'ring-yellow-500', we apply ring logic.
    }

    // 5. Build HTML
    // Determine Aspect Ratio & Size
    let aspectClass = 'aspect-[3/4]';
    if (isBig) aspectClass = 'aspect-[2/1]';
    else if (options.variant === 'hero-vertical') aspectClass = 'h-full min-h-[300px] md:min-h-[400px] aspect-[2/3] md:aspect-auto'; // Mobile: 2/3, Desktop: Auto/Full

    card.innerHTML = `
        <!-- Thumbnail Area -->
        <div class="${aspectClass} relative bg-zinc-800 overflow-hidden ${borderClass} ${options.variant === 'hero-vertical' ? 'h-full' : ''}">
            ${hasImage
            ? `<img src="${imageUrl}" 
                     alt="${series.title || ''}"
                     class="w-full h-full object-cover ${options.variant === 'hero-vertical' ? 'object-top' : ''} transition-transform duration-500 group-hover:scale-105" 
                     loading="lazy"
                     onerror="this.style.display='none'; this.nextElementSibling.style.display='flex'">`
            : ''
        }
            
            <!-- Fallback Icon -->
            <div class="absolute inset-0 flex items-center justify-center bg-zinc-800 text-zinc-700 ${hasImage ? 'hidden' : 'flex'}">
                <span class="material-symbols-outlined text-4xl opacity-50">auto_stories</span>
            </div>

            <!-- Gradient Overlay (Always present for text readability if stat overlay on, or if big) -->
            ${(overlayStats || isBig) ? '<div class="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>' : ''}

            <!-- ----------------------------- -->
            <!-- 🛡️ COMPLIANCE BADGES (Atomic) -->
            <!-- ----------------------------- -->
            <div class="absolute top-2 left-2 flex flex-col gap-1 items-start z-10 pointer-events-none">
                <!-- 1. [Age] Rating Badge (Atomic) -->
                ${(() => {
            // [FIX] Robust Age Badge Logic (Handle String/Number/Null)
            // [FIX] Robust Age Badge: Default to 0 (All) if missing/NaN, unless Adult
            let limit = parseInt(series.ageLimit, 10);
            if (isNaN(limit)) {
                limit = series.isAdult ? 19 : 0;
            }
            let badge = '';

            // Check explicit values first
            if (!isNaN(limit)) {
                switch (limit) {
                    case 0:
                        badge = `<span class="bg-green-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm border border-green-500 flex items-center gap-0.5">
                                    All
                                 </span>`;
                        break;
                    case 12:
                        badge = `<span class="bg-blue-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm border border-blue-400 flex items-center gap-0.5">
                                    12
                                 </span>`;
                        break;
                    case 15:
                        badge = `<span class="bg-amber-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm border border-amber-500 flex items-center gap-0.5">
                                    15
                                 </span>`;
                        break;
                    case 19:
                        badge = `<span class="bg-red-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm border border-red-500 flex items-center gap-0.5">
                                    <span class="material-symbols-outlined text-[10px]">18_up_rating</span> 19
                                 </span>`;
                        break;
                }
            }

            // Fallback for Legacy/Null Data (use isAdult flag)
            // If explicit badge not set (e.g. limit was NaN or unexpected), fallback to 19+ if isAdult
            if (!badge && (series.isAdult || limit === 19)) {
                badge = `<span class="bg-red-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded shadow-sm border border-red-500 flex items-center gap-0.5">
                             <span class="material-symbols-outlined text-[10px]">18_up_rating</span> 19
                         </span>`;
            }

            return badge || ''; // Return badge or empty string
        })()}
                <!-- 2. [AI] Generated Badge -->
                ${(series.isAI)
            ? `<span class="bg-purple-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm border border-purple-500/50 flex items-center gap-0.5" title="AI Generated">
                         <span class="material-symbols-outlined text-[10px]">smart_toy</span> AI
                       </span>`
            : ''
        }
                 <!-- 3. [Type] Webtoon/Novel (Optional) -->
                ${(showType && !series.isAdult && !series.isAI) // 19/AI가 있으면 굳이 타입까지 보여줄 공간이 부족할 수 있음. 혹은 다 보여줌?
            ? `<span class="bg-black/60 backdrop-blur-md text-zinc-300 text-[10px] font-medium px-1.5 py-0.5 rounded border border-white/10">
                         ${series.type === 'webnovel' ? '소설' : '웹툰'}
                       </span>`
            : ''}
            </div>

            <!-- ----------------------------- -->
            <!-- ⚡ MARKETING BADGES (Event)   -->
            <!-- ----------------------------- -->
            <!-- Golden Time Event Badge (Top Right) -->
            ${(marketing && marketing.multiplier > 1)
            ? `<div class="absolute top-0 right-0 ${marketing.theme.badgeBg || 'bg-yellow-500'} text-white text-[10px] font-black px-2 py-1 rounded-bl-xl shadow-lg flex items-center gap-1 z-20 animate-pulse">
                     <span class="material-symbols-outlined text-[12px] font-bold fill-current">bolt</span> x${marketing.multiplier}
                   </div>`
            : ''
        }

            <!-- ----------------------------- -->
            <!-- 🏅 RANKING / NEW BADGES       -->
            <!-- ----------------------------- -->
            ${(rank)
            ? `<div class="absolute top-0 left-0 ${getRankColor(rank)} text-white font-bold text-xs px-2.5 py-1 rounded-br-xl z-20 shadow-md">
                     ${rank}
                   </div>`
            : ''
        }
            
            ${(badges.includes('new') && !rank)
            ? `<div class="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-10 animate-pulse">NEW</div>`
            : ''
        }

             ${(badges.includes('hot') && !rank)
            ? `<div class="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-10 animate-bounce">HOT</div>`
            : ''
        }

            <!-- ----------------------------- -->
            <!-- 🆔 TYPE BADGES (Bottom Right) -->
            <!-- ----------------------------- -->
            ${(series.type === 'davinci')
            ? `<div class="absolute bottom-2 right-2 bg-fuchsia-600/90 backdrop-blur-md text-white text-[10px] font-black px-2 py-1 rounded shadow-lg border border-fuchsia-400/50 z-20 flex items-center gap-1">
                 <span class="material-symbols-outlined text-[12px]">movie</span> Cine-Novel
               </div>`
            : ''
        }

            <!-- ----------------------------- -->
            <!-- 📊 STATS OVERLAY (Bottom)     -->
            <!-- ----------------------------- -->
            ${(overlayStats)
            ? `<div class="absolute bottom-2 left-2 right-2 flex items-center justify-between text-white text-[10px] font-medium opacity-90">
                     <div class="flex items-center gap-2">
                        <span class="flex items-center gap-0.5"><span class="material-symbols-outlined text-[12px]">favorite</span> ${(series.stats?.likeCount || 0).toLocaleString()}</span>
                        <span class="flex items-center gap-0.5"><span class="material-symbols-outlined text-[12px]">visibility</span> ${(Math.max(series.stats?.viewCount || 0, series.viewCount || 0, series.views || 0)).toLocaleString()}</span>
                     </div>
                   </div>`
            : ''
        }

            <!-- Big Card Title Overlay -->
            ${(isBig || options.variant === 'hero-vertical')
            ? `<div class="absolute bottom-0 inset-x-0 p-4">
                     <div class="flex items-center gap-2 mb-1">
                        ${badges.includes('editor') ? '<span class="bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Editor\'s Pick</span>' : ''}
                     </div>
                     <h3 class="text-2xl font-bold text-white mb-1 drop-shadow-md">${series.title}</h3>
                     <p class="text-xs text-gray-300 line-clamp-2">${series.authorName} • ${(series.description || '').substring(0, 50)}...</p>
                   </div>`
            : ''
        }
        </div>

        <!-- Meta Info (Outside Thumbnail) -->
        ${(!isBig && options.variant !== 'hero-vertical')
            ? `<div class="p-2 md:p-3">
                 <h3 class="font-bold text-sm text-gray-900 dark:text-gray-100 truncate leading-tight mb-0.5">${series.title}</h3>
                 <p class="text-xs text-gray-500 dark:text-gray-400 truncate">${series.authorName || 'Unknown'}</p>
               </div>`
            : ''
        }
    `;

    return card;
};

// Helper: Rank Colors
const getRankColor = (rank) => {
    if (rank === 1) return "bg-yellow-500";
    if (rank === 2) return "bg-gray-400"; // Silver
    if (rank === 3) return "bg-orange-600"; // Bronze
    return "bg-gray-700"; // Others
};
